import { get, set, del, clear } from 'idb-keyval';
import { ThemeSettings } from './themeService';

export interface ProfileSettings {
  restaurantName: string;
  email: string;
  prepTime: string;
  terminalName: string;
  siretMessage: string;
  tvaMessage: string;
  restaurantLogo: string;
  logoPosition: 'before' | 'after';
  /** Ne pas afficher le logo dans l'en-tête de la borne. Absent = true (masqué par défaut). */
  hideLogo?: boolean;
  /** Ne pas afficher le nom dans l'en-tête de la borne. Absent = false (nom affiché). */
  hideName?: boolean;
}

export interface PaymentMethodSettings {
  enableCashPayment: boolean;
  enableCardPayment: boolean;
  // v5: Si activé et qu'UN SEUL mode de paiement est actif → la commande passe
  // directement sans afficher le popup de sélection (évite la confusion utilisateur)
  autoSubmitIfSinglePayment?: boolean;
}

export interface CartTimeoutSettings {
  inactivityTimeout: number;
  cartWarningDuration: number;
}

export interface StandbySettings {
  timeoutSeconds: number;
  slideshowInterval: number;
  isStandbyEnabled: boolean;
}

export type TouchSensitivity = 'normal' | 'high' | 'very-high';
export type ScrollMode = 'normal' | 'smooth' | 'snap';

export interface TouchSettings {
  touchSensitivity: TouchSensitivity;
  scrollMode: ScrollMode;
  animationSpeed: number;
  gpuAcceleration: boolean;
}

export interface StorageSettings {
  profileSettings: ProfileSettings | null;
  paymentSettings: PaymentMethodSettings | null;
  printSettings: 'manual' | 'auto' | 'none' | null;
  baseLanguage: string | null;
  googleApiKey: string | null;
  isTranslationEnabled: boolean;
  cartTimeoutSettings: CartTimeoutSettings | null;
  standbySettings: StandbySettings | null;
  themeSettings: ThemeSettings | null;
  touchSettings: TouchSettings | null;
}

class SettingsStorageService {
  private readonly STORAGE_KEYS = {
    PROFILE: 'profileSettings',
    PAYMENT: 'paymentSettings',
    PRINT: 'printSettings',
    BASE_LANGUAGE: 'baseLanguage',
    GOOGLE_API_KEY: 'googleApiKey',
    TRANSLATION_ENABLED: 'isTranslationEnabled',
    TERMINAL_NAME: 'terminalName',
    CART_TIMEOUT: 'cartTimeoutSettings',
    STANDBY: 'standbySettings',
    THEME: 'themeSettings',
    TOUCH: 'touchSettings',
  };

  private readonly DEFAULT_PROFILE_SETTINGS: ProfileSettings = {
    restaurantName: '',
    email: '',
    prepTime: '',
    terminalName: '',
    siretMessage: '',
    tvaMessage: '',
    restaurantLogo: '',
    logoPosition: 'before',
    hideLogo: true,
    hideName: false,
  };

  private readonly DEFAULT_PAYMENT_SETTINGS: PaymentMethodSettings = {
    enableCashPayment: true,
    enableCardPayment: true,
    autoSubmitIfSinglePayment: false, // v5: désactivé par défaut (comportement actuel inchangé)
  };

  private readonly DEFAULT_CART_TIMEOUT_SETTINGS: CartTimeoutSettings = {
    inactivityTimeout: 60,
    cartWarningDuration: 10,
  };

  private readonly DEFAULT_STANDBY_SETTINGS: StandbySettings = {
    timeoutSeconds: 60,
    slideshowInterval: 5000,
    isStandbyEnabled: false, // écran de veille DÉSACTIVÉ par défaut (activable dans l'admin)
  };

  private readonly DEFAULT_TOUCH_SETTINGS: TouchSettings = {
    touchSensitivity: 'normal',
    scrollMode: 'smooth',
    animationSpeed: 150,
    gpuAcceleration: true,
  };

  async getProfileSettings(): Promise<ProfileSettings> {
    try {
      const stored = await get<ProfileSettings>(this.STORAGE_KEYS.PROFILE);
      if (stored) {
        console.log('[SettingsStorage] Profile loaded from IndexedDB:', {
          siretMessage: stored.siretMessage,
          tvaMessage: stored.tvaMessage,
        });
        return {
          restaurantName: stored.restaurantName || '',
          email: stored.email || '',
          prepTime: stored.prepTime || '',
          terminalName: stored.terminalName || '',
          siretMessage: stored.siretMessage || '',
          tvaMessage: stored.tvaMessage || '',
          restaurantLogo: stored.restaurantLogo || '',
          logoPosition: stored.logoPosition || 'before',
          hideLogo: stored.hideLogo ?? true,
          hideName: stored.hideName ?? false,
        };
      }
    } catch (error) {
      console.error('[SettingsStorage] Error loading profile settings:', error);
    }

    const terminalName = await this.getOrCreateTerminalName();
    return {
      ...this.DEFAULT_PROFILE_SETTINGS,
      terminalName,
    };
  }

  async saveProfileSettings(settings: ProfileSettings): Promise<boolean> {
    try {
      await set(this.STORAGE_KEYS.PROFILE, settings);
      await set(this.STORAGE_KEYS.TERMINAL_NAME, settings.terminalName);
      console.log('[SettingsStorage] Profile saved to IndexedDB:', {
        siretMessage: settings.siretMessage,
        tvaMessage: settings.tvaMessage,
      });
      return true;
    } catch (error) {
      console.error('[SettingsStorage] Error saving profile settings:', error);
      return false;
    }
  }

  async getPaymentSettings(): Promise<PaymentMethodSettings> {
    try {
      const stored = await get<PaymentMethodSettings>(this.STORAGE_KEYS.PAYMENT);
      if (stored) {
        console.log('[SettingsStorage] Payment settings loaded from IndexedDB');
        // v5: fusion avec les défauts pour ajouter les nouveaux champs manquants
        return { ...this.DEFAULT_PAYMENT_SETTINGS, ...stored };
      }
    } catch (error) {
      console.error('[SettingsStorage] Error loading payment settings:', error);
    }
    return this.DEFAULT_PAYMENT_SETTINGS;
  }

  async savePaymentSettings(settings: PaymentMethodSettings): Promise<boolean> {
    try {
      await set(this.STORAGE_KEYS.PAYMENT, settings);
      console.log('[SettingsStorage] Payment settings saved to IndexedDB');
      return true;
    } catch (error) {
      console.error('[SettingsStorage] Error saving payment settings:', error);
      return false;
    }
  }

  async getPrintSettings(): Promise<'manual' | 'auto' | 'none'> {
    try {
      const stored = await get<string>(this.STORAGE_KEYS.PRINT);
      if (stored && ['manual', 'auto', 'none'].includes(stored)) {
        console.log('[SettingsStorage] Print settings loaded from IndexedDB');
        return stored as 'manual' | 'auto' | 'none';
      }
    } catch (error) {
      console.error('[SettingsStorage] Error loading print settings:', error);
    }
    return 'manual';
  }

  async savePrintSettings(settings: 'manual' | 'auto' | 'none'): Promise<boolean> {
    try {
      await set(this.STORAGE_KEYS.PRINT, settings);
      console.log('[SettingsStorage] Print settings saved to IndexedDB');
      return true;
    } catch (error) {
      console.error('[SettingsStorage] Error saving print settings:', error);
      return false;
    }
  }

  async getBaseLanguage(): Promise<string> {
    try {
      const stored = await get<string>(this.STORAGE_KEYS.BASE_LANGUAGE);
      if (stored) {
        console.log('[SettingsStorage] Base language loaded from IndexedDB');
        return stored;
      }
    } catch (error) {
      console.error('[SettingsStorage] Error loading base language:', error);
    }
    return 'fr';
  }

  async saveBaseLanguage(language: string): Promise<boolean> {
    try {
      await set(this.STORAGE_KEYS.BASE_LANGUAGE, language);
      console.log('[SettingsStorage] Base language saved to IndexedDB');
      return true;
    } catch (error) {
      console.error('[SettingsStorage] Error saving base language:', error);
      return false;
    }
  }

  async getGoogleApiKey(): Promise<string> {
    try {
      const stored = await get<string>(this.STORAGE_KEYS.GOOGLE_API_KEY);
      if (stored) {
        console.log('[SettingsStorage] Google API key loaded from IndexedDB');
        return stored;
      }
    } catch (error) {
      console.error('[SettingsStorage] Error loading Google API key:', error);
    }
    return '';
  }

  async saveGoogleApiKey(key: string): Promise<boolean> {
    try {
      await set(this.STORAGE_KEYS.GOOGLE_API_KEY, key);
      console.log('[SettingsStorage] Google API key saved to IndexedDB');
      return true;
    } catch (error) {
      console.error('[SettingsStorage] Error saving Google API key:', error);
      return false;
    }
  }

  async getTranslationEnabled(): Promise<boolean> {
    try {
      const stored = await get<boolean>(this.STORAGE_KEYS.TRANSLATION_ENABLED);
      if (stored !== undefined) {
        console.log('[SettingsStorage] Translation enabled loaded from IndexedDB');
        return stored;
      }
    } catch (error) {
      console.error('[SettingsStorage] Error loading translation enabled:', error);
    }
    return true;
  }

  async saveTranslationEnabled(enabled: boolean): Promise<boolean> {
    try {
      await set(this.STORAGE_KEYS.TRANSLATION_ENABLED, enabled);
      console.log('[SettingsStorage] Translation enabled saved to IndexedDB');
      return true;
    } catch (error) {
      console.error('[SettingsStorage] Error saving translation enabled:', error);
      return false;
    }
  }

  async getCartTimeoutSettings(): Promise<CartTimeoutSettings> {
    try {
      const stored = await get<CartTimeoutSettings>(this.STORAGE_KEYS.CART_TIMEOUT);
      if (stored) {
        console.log('[SettingsStorage] Cart timeout settings loaded from IndexedDB');
        return stored;
      }
    } catch (error) {
      console.error('[SettingsStorage] Error loading cart timeout settings:', error);
    }
    return this.DEFAULT_CART_TIMEOUT_SETTINGS;
  }

  async saveCartTimeoutSettings(settings: CartTimeoutSettings): Promise<boolean> {
    try {
      await set(this.STORAGE_KEYS.CART_TIMEOUT, settings);
      console.log('[SettingsStorage] Cart timeout settings saved to IndexedDB');
      return true;
    } catch (error) {
      console.error('[SettingsStorage] Error saving cart timeout settings:', error);
      return false;
    }
  }

  async getStandbySettings(): Promise<StandbySettings> {
    try {
      const stored = await get<StandbySettings>(this.STORAGE_KEYS.STANDBY);
      if (stored) {
        console.log('[SettingsStorage] Standby settings loaded from IndexedDB');
        return stored;
      }
    } catch (error) {
      console.error('[SettingsStorage] Error loading standby settings:', error);
    }
    return this.DEFAULT_STANDBY_SETTINGS;
  }

  async saveStandbySettings(settings: StandbySettings): Promise<boolean> {
    try {
      await set(this.STORAGE_KEYS.STANDBY, settings);
      console.log('[SettingsStorage] Standby settings saved to IndexedDB');
      return true;
    } catch (error) {
      console.error('[SettingsStorage] Error saving standby settings:', error);
      return false;
    }
  }

  async getThemeSettings(): Promise<ThemeSettings> {
    try {
      const stored = await get<ThemeSettings>(this.STORAGE_KEYS.THEME);
      if (stored) {
        console.log('[SettingsStorage] Theme settings loaded from IndexedDB');
        return stored;
      }
    } catch (error) {
      console.error('[SettingsStorage] Error loading theme settings:', error);
    }
    return { templateId: 'default' };
  }

  async saveThemeSettings(settings: ThemeSettings): Promise<boolean> {
    try {
      if (settings.background && settings.background.type !== 'none' && settings.background.data) {
        const bgType = settings.background.type;
        const storageKey = `theme-background-${bgType}`;
        await set(storageKey, settings.background.data);
        console.log(`[SettingsStorage] Background ${bgType} data saved to IndexedDB`);
      }

      const settingsToStore = { ...settings };
      if (settingsToStore.background) {
        settingsToStore.background = {
          ...settingsToStore.background,
          data: undefined,
        };
      }

      await set(this.STORAGE_KEYS.THEME, settingsToStore);
      console.log('[SettingsStorage] Theme settings saved to IndexedDB');
      return true;
    } catch (error) {
      console.error('[SettingsStorage] Error saving theme settings:', error);
      return false;
    }
  }

  async getTouchSettings(): Promise<TouchSettings> {
    try {
      const stored = await get<TouchSettings>(this.STORAGE_KEYS.TOUCH);
      if (stored) {
        console.log('[SettingsStorage] Touch settings loaded from IndexedDB');
        return stored;
      }
    } catch (error) {
      console.error('[SettingsStorage] Error loading touch settings:', error);
    }
    return this.DEFAULT_TOUCH_SETTINGS;
  }

  async saveTouchSettings(settings: TouchSettings): Promise<boolean> {
    try {
      await set(this.STORAGE_KEYS.TOUCH, settings);
      console.log('[SettingsStorage] Touch settings saved to IndexedDB');
      return true;
    } catch (error) {
      console.error('[SettingsStorage] Error saving touch settings:', error);
      return false;
    }
  }

  async getAllSettings(): Promise<StorageSettings> {
    return {
      profileSettings: await this.getProfileSettings(),
      paymentSettings: await this.getPaymentSettings(),
      printSettings: await this.getPrintSettings(),
      baseLanguage: await this.getBaseLanguage(),
      googleApiKey: await this.getGoogleApiKey(),
      isTranslationEnabled: await this.getTranslationEnabled(),
      cartTimeoutSettings: await this.getCartTimeoutSettings(),
      standbySettings: await this.getStandbySettings(),
      themeSettings: await this.getThemeSettings(),
      touchSettings: await this.getTouchSettings(),
    };
  }

  async saveAllSettings(settings: {
    profileSettings?: ProfileSettings;
    paymentSettings?: PaymentMethodSettings;
    printSettings?: 'manual' | 'auto' | 'none';
    baseLanguage?: string;
    googleApiKey?: string;
    isTranslationEnabled?: boolean;
    cartTimeoutSettings?: CartTimeoutSettings;
    standbySettings?: StandbySettings;
    themeSettings?: ThemeSettings;
    touchSettings?: TouchSettings;
  }): Promise<boolean> {
    let success = true;

    try {
      if (settings.profileSettings) {
        success = (await this.saveProfileSettings(settings.profileSettings)) && success;
      }
      if (settings.paymentSettings) {
        success = (await this.savePaymentSettings(settings.paymentSettings)) && success;
      }
      if (settings.printSettings) {
        success = (await this.savePrintSettings(settings.printSettings)) && success;
      }
      if (settings.baseLanguage !== undefined) {
        success = (await this.saveBaseLanguage(settings.baseLanguage)) && success;
      }
      if (settings.googleApiKey !== undefined) {
        success = (await this.saveGoogleApiKey(settings.googleApiKey)) && success;
      }
      if (settings.isTranslationEnabled !== undefined) {
        success = (await this.saveTranslationEnabled(settings.isTranslationEnabled)) && success;
      }
      if (settings.cartTimeoutSettings) {
        success = (await this.saveCartTimeoutSettings(settings.cartTimeoutSettings)) && success;
      }
      if (settings.standbySettings) {
        success = (await this.saveStandbySettings(settings.standbySettings)) && success;
      }
      if (settings.themeSettings) {
        success = (await this.saveThemeSettings(settings.themeSettings)) && success;
      }
      if (settings.touchSettings) {
        success = (await this.saveTouchSettings(settings.touchSettings)) && success;
      }
    } catch (error) {
      console.error('[SettingsStorage] Error in saveAllSettings:', error);
      return false;
    }

    return success;
  }

  private async getOrCreateTerminalName(): Promise<string> {
    try {
      let terminalName = await get<string>(this.STORAGE_KEYS.TERMINAL_NAME);
      if (!terminalName) {
        const terminalNumber = Math.floor(Math.random() * 99) + 1;
        terminalName = `Borne ${terminalNumber}`;
        await set(this.STORAGE_KEYS.TERMINAL_NAME, terminalName);
      }
      return terminalName;
    } catch (error) {
      console.error('[SettingsStorage] Error with terminal name:', error);
      return 'Borne 1';
    }
  }

  async clearAllSettings(): Promise<boolean> {
    try {
      await clear();
      console.log('[SettingsStorage] All settings cleared from IndexedDB');
      return true;
    } catch (error) {
      console.error('[SettingsStorage] Error clearing settings:', error);
      return false;
    }
  }

  async exportSettings(): Promise<string> {
    try {
      const allSettings = await this.getAllSettings();
      return JSON.stringify(allSettings, null, 2);
    } catch (error) {
      console.error('[SettingsStorage] Error exporting settings:', error);
      return '{}';
    }
  }

  async importSettings(jsonString: string): Promise<boolean> {
    try {
      const settings = JSON.parse(jsonString);
      return await this.saveAllSettings(settings);
    } catch (error) {
      console.error('[SettingsStorage] Error importing settings:', error);
      return false;
    }
  }
}

export const settingsStorage = new SettingsStorageService();
